import { Table, Form, InputGroup, Button } from "react-bootstrap";
import { useState, useEffect, useCallback, useRef } from "react";
import "../../../../common/styles/table.css";
import LessonTableRow from "./LessonTableRow";
import { useGetAllLessons } from "../../../../hooks/teacher/lessons/useGetAllLessons";
import {
  ITEMS_PER_PAGE,
  SEARCH_DEBOUNCE_TIME,
} from "../../../../constant/constant";
import TablePagination from "../pagination/TablePagination";
import TableSkeleton from "../../common/TableSkeleton";
import TableError from "../../common/TableError";
import RefreshButtonTable from "../../../common/RefreshButtonTable";

/**
 * @param {{ type: "Teacher" | "School" }} props
 */

const LessonTable = ({ type }) => {
  const { loading, error, lessons, meta, getAllLessons } = useGetAllLessons();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimeoutRef = useRef(null);

  /**
   * Build the filters object for the API call.
   */
  const buildFilters = useCallback((search) => {
    if (!search || search.trim() === "") return {};
    return { title: search.trim() };
  }, []);

  /**
   * Fetch lessons with current page and search filters
   */
  const fetchLessons = useCallback(
    (page, search) => {
      getAllLessons({
        page,
        per_page: ITEMS_PER_PAGE,
        filters: buildFilters(search),
      });
    },
    [buildFilters],
  );

  // Fetch lessons on initial load and when page or search changes
  useEffect(() => {
    fetchLessons(currentPage, debouncedSearch);
  }, [currentPage, debouncedSearch]);

  /**
   * Handle search input change with debounce
   */
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1); // Reset to first page on new search
      setDebouncedSearch(value);
    }, SEARCH_DEBOUNCE_TIME);
  };

  /**
   * Clear search input and reset results
   */
  const handleClearSearch = () => {
    setSearchTerm("");
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setCurrentPage(1);
    setDebouncedSearch("");
  };

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleRefresh = () => {
    fetchLessons(currentPage, debouncedSearch);
  };

  /**
   * Refresh after delete - handles the edge case when deleting
   * the last item on a page by going back to the previous page
   */
  const handleRefreshAfterDelete = () => {
    if (lessons.length === 1 && currentPage > 1) {
      // Go back to previous page (useEffect will trigger the fetch)
      setCurrentPage(currentPage - 1);
    } else {
      // Stay on current page and refresh
      fetchLessons(currentPage, debouncedSearch);
    }
  };

  // Show skeleton only on initial load (when lessons is undefined and loading)
  if (loading && lessons === undefined) {
    return <TableSkeleton rows={ITEMS_PER_PAGE} columns={5} />;
  }

  if (error || lessons === null) {
    return (
      <TableError
        error={error}
        handleRefresh={handleRefresh}
        loading={loading}
      />
    );
  }

  const totalPages = meta?.last_page || 1;
  const hasLessons = lessons && lessons.length > 0;
  const isSearching = debouncedSearch.trim() !== "";

  return (
    <div>
      {/* Search and Refresh Row */}
      <div className="d-flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
        <InputGroup style={{ maxWidth: "400px" }}>
          <InputGroup.Text>
            <i className="bi bi-search"></i>
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search by lesson name..."
            value={searchTerm}
            onChange={handleSearchChange}
            disabled={loading && !isSearching && lessons === undefined}
            className="shadow-none"
          />
          {searchTerm && (
            <Button
              variant="outline-secondary"
              onClick={handleClearSearch}
              disabled={loading}
              title="Clear search"
            >
              <i className="bi bi-x-lg"></i>
            </Button>
          )}
        </InputGroup>

        <RefreshButtonTable handleRefresh={handleRefresh} loading={loading} />
      </div>

      {!hasLessons ? (
        <div className="alert alert-info mt-3" role="alert">
          {isSearching
            ? `No lessons found matching "${debouncedSearch}".`
            : "No lessons found."}
        </div>
      ) : (
        <div className="custom-table-container mt-3">
          <div className="custom-table-wrapper">
            <Table hover className="custom-table">
              <thead className="custom-table-head">
                <tr>
                  <th>Lessons</th>
                  <th>Description</th>
                  <th>Quest Count</th>
                  <th>Classroom Count</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {lessons.map((lesson) => (
                  <LessonTableRow
                    key={lesson.id}
                    lesson={lesson}
                    refetch={handleRefreshAfterDelete}
                    disabled={loading}
                    type={type}
                  />
                ))}
              </tbody>
            </Table>
          </div>

          <TablePagination
            totalPage={totalPages}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            disabled={loading}
          />
        </div>
      )}
    </div>
  );
};

export default LessonTable;
